import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';

import { outputIfNoVariables } from '../utilities/misc';
import { rem } from '../utilities/style';
import { fontWeights } from '../theme/type';
import { colors } from '../theme/colors';
import { media } from '../theme/media';
import { body, headers, richTextStyles } from '../styles/type';

import ButtonGroup from './ButtonGroup';
import Button from './buttons/Button';
import ButtonText from './buttons/ButtonText';
import TextRenderer from './TextRenderer';

const Container = styled.div`
  position: relative;
  padding: ${rem(24)};
  border-radius: ${rem(10)};
  // safari fix https://gist.github.com/domske/b66047671c780a238b51c51ffde8d3a0
  z-index: 0;
  grid-column: span 2;
  overflow: hidden;
  background-color: ${colors.blue['500']};

  ${media('>tiny')} {
    padding: ${rem(32)};
  }

  ${media('>small')} {
    padding: ${rem(24)};
    grid-column: span 1;
  }

  ${media('>smedium')} {
    grid-column: span 1;
    padding: ${rem(32)};

    &:nth-of-type(4n+2) {
      grid-column: span 2;
    }
    &:nth-of-type(4n+3) {
      grid-column: span 2;
    }
  }
`;

const BackgroundImage = styled(GatsbyImage)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: start;
  max-width: ${rem(412)};
`;

const Pill = styled.div`
  ${body.small}
  font-weight: ${fontWeights.semiBold};
  background-color: ${colors.blue['800']};
  color: ${colors.white};
  border-radius: ${rem(100)};
  padding: ${rem(4)} ${rem(12)};
  margin-bottom: ${rem(16)};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.24);
`;

const Headline = styled.h3`
  ${headers.h3}
  color: ${colors.white};
  margin-top: auto;
  margin-bottom: ${rem(12)};
`;

const Subhead = styled.p`
  ${headers.eyebrow}
  color: ${colors.gray['50']};
  margin-bottom: ${rem(16)};
`;

const Text = styled.div`
  ${body.regular}
  ${richTextStyles}
  margin-bottom: ${rem(16)};
`;

const OffsetCard = ({
  badgeText,
  headline,
  subhead,
  backgroundImage,
  text,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
}) => (
  <Container>
    {backgroundImage && (
      <BackgroundImage
        image={getImage(backgroundImage)}
        alt={backgroundImage.description}
        objectFit="cover"
        objectPosition="center right"
      />
    )}
    <Content>
      {badgeText && (
        <Pill>
          <TextRenderer text={badgeText} />
        </Pill>
      )}
      {headline && (
        <Headline>
          <TextRenderer text={headline} />
        </Headline>
      )}
      {subhead && (
        <Subhead>
          <TextRenderer text={subhead} />
        </Subhead>
      )}
      {text && (
        <Text lighter>
          <TextRenderer text={text} richText />
        </Text>
      )}
      <ButtonGroup>
        {primaryCtaText && primaryCtaLink && (
          <Button
            to={primaryCtaLink}
            aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
            light
            size="small"
          >
            <TextRenderer text={primaryCtaText} />
          </Button>
        )}
        {secondaryCtaText && secondaryCtaLink && (
          <ButtonText
            to={secondaryCtaLink}
            aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
            color="light"
          >
            <TextRenderer text={secondaryCtaText} />
          </ButtonText>
        )}
      </ButtonGroup>
    </Content>
  </Container>
);

OffsetCard.propTypes = {
  badgeText: PropTypes.string,
  headline: PropTypes.string,
  subhead: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  backgroundImage: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    gatsbyImageData: PropTypes.object
  }),
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
};

export const query = graphql`
  fragment OffsetCardFragment on ContentfulOffsetCard {
    badgeText
    headline
    subhead
    text {
      raw
    }
    backgroundImage {
      gatsbyImageData(width: 860, height: 420, quality: 75, resizingBehavior: FILL)
      description
    }
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
  }
`;

export default OffsetCard;
